import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logging-kit';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertService } from '../services/alert.service';
import { BaseTokenService } from '../services/base-token.service';
import { LoadingService } from '../services/loading.service';
import { SessionStorageService } from '../services/session-storage.service';

// Context token - hangi status kodlarında alert gösterilmeyecek
export const SKIP_ERROR_ALERT_STATUS_CODES = new HttpContextToken<number[]>(() => []);

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
	const logger = inject(NGXLogger);
	const alertService = inject(AlertService);
	const loadingService = inject(LoadingService);
	const sessionStorageService = inject(SessionStorageService);
	const injector = inject(Injector);
	const router = inject(Router);
	const tokenService = inject(BaseTokenService, { optional: true });

	return next(req).pipe(
		catchError((error: HttpErrorResponse) => {
			logger.error('error: ', error);
			loadingService.hideLoading();

			// Context token kontrolü - belirli status kodlarında alert gösterilmesin
			const skipStatusCodes = req.context.get(SKIP_ERROR_ALERT_STATUS_CODES);
			const shouldSkipAlert = skipStatusCodes.length > 0 && skipStatusCodes.includes(error.status);

			if (shouldSkipAlert) {
				// Alert gösterme, sadece error'u throw et
				return throwError(() => error);
			}

			try {
				const translateService = injector.get(TranslateService);
				if (error.status === 0) {
					alertService.error(
						translateService.instant('g.error'),
						translateService.instant('error.service-unavailable')
					);
				} else if (error.status == 408) {
					if (tokenService) {
						tokenService.isTokenExpired.next(true);
					}
					sessionStorageService.clearAll();
					router.navigateByUrl('/login');
				} else if (error.status == 401) {
					alertService.error(translateService.instant('g.error'), error.error.message);
				} else if (error.status === 403) {
					alertService.error(translateService.instant('g.error'), translateService.instant('error.forbidden'));
				} else if (error.status === 423) {
					sessionStorageService.clearAll();
					router.navigateByUrl('/login');
				} else if (error.status === 422) {
					handleValidationErrors(error, alertService, translateService);
				} else if (error.status === 413) {
					alertService.error(
						translateService.instant('g.error'),
						translateService.instant('error.request-entity-too-large')
					);
				} else if (error.status === 400) {
					alertService.error(translateService.instant('g.error'), translateService.instant('error.bad-request'));
				} else {
					prepareCustomApiError(error, alertService, injector);
				}
			} catch (e: unknown) {
				logger.error(e);
				const translateService = injector.get(TranslateService);
				alertService.error(translateService.instant('g.error'), 'Error.... ');
			}

			return throwError(() => error);
		})
	);
};

function handleValidationErrors(
	error: HttpErrorResponse,
	alertService: AlertService,
	translateService: TranslateService
): void {
	if (error?.error?.errorItems && error?.error?.errorItems.length > 0) {
		showCustomApiErrorMessageDirectly(error, alertService, translateService);
	} else {
		alertService.error(translateService.instant('g.error'), error.error.message);
	}
}

function prepareCustomApiError(err: HttpErrorResponse, alertService: AlertService, injector: Injector): void {
	if (err?.error?.errorItems && err?.error?.errorItems.length > 0) {
		err.error.errorItems.forEach((inner: HttpErrorResponse) => {
			showCustomApiErrorMessage(inner, alertService, injector);
		});
	} else {
		showCustomApiErrorMessage(err, alertService, injector);
	}
}

function showCustomApiErrorMessage(err: HttpErrorResponse, alertService: AlertService, injector: Injector): void {
	const translateService = injector.get(TranslateService);
	const { spanId, traceId } = err.error || {};
	let extra = '';
	if (spanId) {
		extra = '\n\nspanId : ' + spanId + '\ntraceId : ' + traceId;
	}
	const message = (err.error?.message || '') + extra;
	alertService.error(translateService.instant('g.error'), message);
}

function showCustomApiErrorMessageDirectly(
	error: HttpErrorResponse,
	alertService: AlertService,
	translateService: TranslateService
): void {
	const { spanId, traceId } = error.error;
	error?.error?.errorItems.forEach((err: HttpErrorResponse) => {
		let extra = '';
		if (spanId) {
			extra = `\n\nspanId : ${spanId}\ntraceId : ${traceId}`;
		}
		alertService.error(translateService.instant('g.error'), `${err.message}${extra}`);
	});
}
